/**
 * index.js - 2022-02-27 03:32
 *
 * Copyright © 2022 Libao Jin <jinlibao@outlook.com>
 * Distributed under terms of the MIT license.
 */

import React from 'react';
import ReactDOM from 'react-dom';
import ReactMarkdown from 'react-markdown';
import SlideShow from 'react-slideshow-ui';
import rehypeRaw from 'rehype-raw';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import Markup from 'interweave';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { styled } from '@mui/system';
import TabsUnstyled from '@mui/base/TabsUnstyled';
import TabsListUnstyled from '@mui/base/TabsListUnstyled';
import TabPanelUnstyled from '@mui/base/TabPanelUnstyled';
import { buttonUnstyledClasses } from '@mui/base/ButtonUnstyled';
import TabUnstyled, { tabUnstyledClasses } from '@mui/base/TabUnstyled';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import {
  BrowserRouter,
  Routes, // instead of "Switch"
  Route,
  useParams,
} from "react-router-dom";
import './style.css';
import 'katex/dist/katex.min.css'

let basename = '/v3';
let data_dir='https://leetcode.libaoj.in/data';

const lang = {
  cpp: {name: 'C++', slug: 'cpp', highlight: 'cpp'},
  java: {name: 'Java', slug: 'java', highlight: 'java'},
  python: {name: 'Python', slug: 'python', highlight: 'python'},
  python3: {name: 'Python3', slug: 'python3', highlight: 'python'},
  c: {name: 'C', slug: 'c', highlight: 'c'},
  csharp: {name: 'C#', slug: 'csharp', highlight: 'csharp'},
  javascript: {name: 'JavaScript', slug: 'javascript', highlight: 'javascript'},
  ruby: {name: 'Ruby', slug: 'ruby', highlight: 'ruby'},
  swift: {name: 'Swift', slug: 'swift', highlight: 'swift'},
  golang: {name: 'Go', slug: 'golang', highlight: 'go'},
  scala: {name: 'Scala', slug: 'scala', highlight: 'scala'},
  kotlin: {name: 'Kotlin', slug: 'kotlin', highlight: 'kotlin'},
  rust: {name: 'Rust', slug: 'rust', highlight: 'rust'},
  php: {name: 'PHP', slug: 'php', highlight: 'php'},
  typescript: {name: 'TypeScript', slug: 'typescript', highlight: 'typescript'},
  racket: {name: 'Racket', slug: 'racket', highlight: 'racket'},
  erlang: {name: 'Erlang', slug: 'erlang', highlight: 'erlang'},
  elixir: {name: 'Elixir', slug: 'elixir', highlight: 'elixir'},
  dart: {name: 'Dart', slug: 'dart', highlight: 'dart'},
  mysql: {name: 'MySQL', slug: 'mysql', highlight: 'sql'},
  mssql: {name: 'MS SQL Server', slug: 'mssql', highlight: 'sql'},
  oraclesql: {name: 'Oracle', slug: 'oraclesql', highlight: 'sql'},
  bash: {name: 'Bash', slug: 'bash', highlight: 'bash'},
};

const gray = {
  50: '#AAAAAA',
  100: '#999999',
  200: '#888888',
  300: '#777777',
  400: '#666666',
  500: '#555555',
  600: '#444444',
  700: '#333333',
  800: '#1e1e1e',
  900: '#111111',
};

const Tab = styled(TabUnstyled)`
  font-family: IBM Plex Sans, sans-serif;
  color: ${gray[100]};
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: bold;
  background-color: transparent;
  width: 100%;
  padding: 12px;
  margin: 0px;
  border: none;
  border-radius: 0px;
  display: flex;
  justify-content: center;

  &:hover {
    background-color: ${gray[400]};
    color: white;
  }

  &.${tabUnstyledClasses.selected} {
    background-color: ${gray[800]};
    color: white;
  }

  &.${buttonUnstyledClasses.disabled} {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TabPanel = styled(TabPanelUnstyled)`
  width: 100%;
  font-family: IBM Plex Sans, sans-serif;
  font-size: 0.875rem;
`;

const TabsList = styled(TabsListUnstyled)`
  min-width: 320px;
  background-color: ${gray[800]};
  border-radius: 0px;
  margin-bottom: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  align-content: space-between;
`;

class QuestionTitle extends React.Component {
  render() {
    let question_data = this.props.question_data;
    let stats = JSON.parse(question_data.stats);
    return (
      <div>
        <h2><Link href={`https://leetcode.com/problems/${question_data.titleSlug}`} target="_blank" color="inherited" underline="none">{question_data.questionFrontendId}. {question_data.title}</Link></h2>
        <span className="prop"> <i className={question_data.paidOnly? "fas fa-lock" : "fas fa-lock-open"}></i> </span>
        <span className="prop"> <i className={question_data.status === 'ac' ? "fas fa-check-square" : "fas fa-square"}></i> </span>
        <span className="prop"> <i className="far fa-thumbs-up"></i> {question_data.likes} </span>
        <span className="prop"> <i className="far fa-thumbs-down"></i> {question_data.dislikes} </span>
        <span className="prop"> <i className="fas fa-check"></i> {stats.totalAcceptedRaw} </span>
        <span className="prop"> <i className="fas fa-upload"></i> {stats.totalSubmissionRaw} </span>
        <span className={question_data.difficulty.toLowerCase()}> {question_data.difficulty} </span>
      </div>
    );
  }
}

class ProblemStatement extends React.Component {
  render() {
    let question_data = this.props.question_data;
    let similarQuestions = JSON.parse(question_data.similarQuestions);
    let companyTagStats = JSON.parse(question_data.companyTagStats);
    let companies = []
    if (companyTagStats) {
      for (let key of Object.keys(companyTagStats)) {
        for (let company of companyTagStats[key]) {
          companies.push(company);
        }
      }
    }
    let codeSnippets = question_data.codeSnippets;
    return (
      <div className='problem_statement'>
        <div className='description'>
          <h3>Description</h3>
          <Markup content={question_data.content.replaceAll('/problems', `${data_dir}/problems`)} />
        </div>
        {question_data.hints && question_data.hints.length > 0 &&
        <div className='hints'>
          <h3>Hints</h3>
          <ol>
            {question_data.hints.map((hint, i) => {
              return (
                <li key={i}>
                  <Markup content={hint.replaceAll('<pre>', '<code>').replaceAll('</pre>', '</code>').replaceAll('/problems', `${data_dir}/problems`)} />
                </li>
              );
            })}
          </ol>
        </div>
        }
        {similarQuestions && similarQuestions.length > 0 &&
          <div>
            <h3>Similar Questions</h3>
            <ul>
              {similarQuestions.map((question, i) => {
                return (
                  <li key={i}>
                    <span className={question.difficulty.toLowerCase()}> {question.difficulty} </span>
                    <Link href={`${basename}/problems/${question.titleSlug}`} color="inherit" underline="none"> {question.title} </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        }
        {question_data.topicTags.length > 0 &&
          <div>
            <h3>Topic</h3>
            <Grid container spacing={1} columns={16}>
              {question_data.topicTags.map((topic, i) => {
                return (
                  <Grid  key={i} container item xs="auto">
                    <Link key={i} href={`${basename}/list/${topic.slug}` } color="inherit" underline="none">
                      <Chip label={topic.name} onClick={() => {}}/>
                    </Link>
                  </Grid>
                );
              })}
            </Grid>
          </div>
        }
        {companies && companies.length > 0 &&
          <div>
            <h3>Company</h3>
            <Grid container spacing={1} columns={16}>
              {companies.map((company, i) => {
                return (
                  <Grid  key={i} container item xs="auto">
                    <Link key={i} href={`${basename}/list/${company.slug}`} color="inherit" underline="none">
                      {company.taggedByAdmin
                        ? <Chip label={`${company.name} | ${company.timesEncountered}`} onClick={() => {}} variant="outlined" />
                        : <Chip label={`${company.name} | ${company.timesEncountered}`} onClick={() => {}} />
                      }
                    </Link>
                  </Grid>
                );
              })}
            </Grid>
          </div>
        }
        {codeSnippets &&
          <div>
            <h3>Code Snippets</h3>
            <TabsUnstyled defaultValue={0}>
              <TabsList style={{position: 'sticky', top: '0'}}>
                {codeSnippets.map((snippet, i) => {
                  return (
                    <Tab key={i}>{snippet.lang}</Tab>
                  );
                })}
              </TabsList>
              {codeSnippets.map((snippet, i) => {
                return (
                  <TabPanel key={i} value={i}>
                    <SyntaxHighlighter
                      customStyle={{
                        marginTop: "0rem",
                      }}
                      language={`${lang[snippet.langSlug]['highlight']}`}
                      showLineNumbers={true}
                      style={vscDarkPlus}>
                      {snippet.code.trim()}
                    </SyntaxHighlighter>
                  </TabPanel>
                )})}
            </TabsUnstyled>
          </div>
        }
      </div>
    );
  }
}

class Solution extends React.Component {
  constructor(props) {
    super(props);
    let content = '';
    let question_note = this.props.question_note;
    if (question_note && question_note.solution && question_note.solution.content) {
      content = question_note.solution.content;
    }
    this.state = {
      slides: [],
      content: content,
      updated: false
    };
  }

  componentDidMount() {
    let content = this.state.content;
    content = this.addSlideShow(content);
    content = content.replaceAll('/problems', `${data_dir}/problems`)
    content = this.extract(content);
    content = this.parse(content);
    let re = /---\s+###/g
    content = content.replaceAll(re, '---\n\n###');
    console.log(content);
    this.setState({
      updated:true,
      content: content
    });
  }

  parse(content) {
    let re = /{:[\w]+="[\w\d]+"}/g;
    let newContent = content.replaceAll(re, '');;
    return newContent;
  }

  extract(content) {
    let re = /<iframe [^>]*><\/iframe>/mg;
    let matches;
    let newContent = content;
    while ((matches = re.exec(content)) !== null) {
      let match;
      let id='aaa';
      let height='400';
      let re_id = /name="([\w\d]+)"/g;
      while ((match = re_id.exec(matches[0])) !== null) {
        id = match[1];
      }
      let re_height = /height="([\d]+)"/g;
      while ((match = re_height.exec(matches[0])) !== null) {
        height = match[1];
      }
      if (id !== 'aaa') {
        let new_iframe = `<iframe src="${basename}/problems/${this.props.question_data.titleSlug}/playground/${id}" height="${height}" name="${id}"></iframe>`;
        let iframe = matches[0];
        newContent = newContent.replace(iframe, new_iframe);
      }
    }
    content = newContent;
    return content;
  }

  convert(match) {
    let images = []
    let re = /\(([/\d\w_.-]+)\)/g;
    let matches;
    while ((matches = re.exec(match)) !== null) {
      images.push(`${matches[1].replaceAll('/problems', `${data_dir}/problems`)}`);
    }
    let slides = this.state.slides;
    slides.push(images);
    this.setState({
      slides: slides
    })
  }

  addSlideShow(content) {
    let newContent = content;
    let re = /\)[\s]+!\[/mg;
    let matches;
    while ((matches = re.exec(content)) !== null) {
      newContent = newContent.replace(matches[0], ')![');
    }
    re = /^!.*\)$/mg;
    content = newContent;
    while ((matches = re.exec(content)) !== null) {
      let count = 0
      for (let c of matches[0]) {
        if (c === '!') ++count;
      }
      if (count > 1) {
        this.convert(matches[0])
        newContent = newContent.replace(matches[0], '');
      }
    }
    content = newContent;
    return content;
  }

  render() {
    if (this.state.updated) {
      let content = this.state.content;
      let slides = this.state.slides;
      return (
        <div className='solution'>
          {slides.length > 0 && slides.map((images, i) => {
            return (
              <SlideShow
                key={i}
                images={images}
                withTimestamp={false}
                pageWillUpdate={(index, image) => {
                }}
              />
            );
          })}
          <ReactMarkdown remarkPlugins={[remarkMath, remarkGfm]} rehypePlugins={[rehypeKatex, rehypeRaw]}>{content}</ReactMarkdown>
        </div>
      );
    } else {
      return (<div></div>);
    }
  }
}

class SubmissionsRuntimeMemory extends React.Component {
  render() {
    return (
      <div>
        <h4>Runtime</h4>
        <TabsUnstyled defaultValue={0}>
          <TabsList>
            {this.props.submission_runtime_memory.runtime && this.props.submission_runtime_memory.runtime.cpp && this.props.submission_runtime_memory.runtime.cpp.map((submission, i) => {
              return (
                <Tab key={i}>{submission.runtime} ms</Tab>
              );
            })}
          </TabsList>
          {this.props.submission_runtime_memory.runtime && this.props.submission_runtime_memory.runtime.cpp && this.props.submission_runtime_memory.runtime.cpp.map((submission, i) => {
            return (
              <TabPanel key={i} value={i}>
                <SyntaxHighlighter
                  customStyle={{
                    marginTop: "0rem",
                  }}
                  language={`${lang[submission.lang]['highlight']}`}
                  showLineNumbers={true}
                  style={vscDarkPlus}>
                  {submission.code.trim()}
                </SyntaxHighlighter>
              </TabPanel>
            )})}
        </TabsUnstyled>
        <h4>Memory</h4>
        <TabsUnstyled defaultValue={0}>
          <TabsList>
            {this.props.submission_runtime_memory.memory && this.props.submission_runtime_memory.memory.cpp && this.props.submission_runtime_memory.memory.cpp.map((submission, i) => {
              return (
                <Tab key={i}>{Math.ceil(submission.memory / 1024 * 100) / 100} KB</Tab>
              );
            })}
          </TabsList>
          {this.props.submission_runtime_memory.memory && this.props.submission_runtime_memory.memory.cpp && this.props.submission_runtime_memory.memory.cpp.map((submission, i) => {
            return (
              <TabPanel key={i} value={i}>
                <SyntaxHighlighter
                  customStyle={{
                    marginTop: "0rem",
                  }}
                  language={`${lang[submission.lang]['highlight']}`}
                  showLineNumbers={true}
                  style={vscDarkPlus}>
                  {submission.code.trim()}
                </SyntaxHighlighter>
              </TabPanel>
            )})}
        </TabsUnstyled>

      </div>
    );
  }
}

class UserSubmissions extends React.Component {
  render() {
    let submissions = this.props.submissions;
    return (
      <TabsUnstyled defaultValue={0}>
        <TabsList>
          {submissions.map((submission, i) => {
            return (
              <Tab key={i}>{i + 1}</Tab>
            );
          })}
        </TabsList>
        {submissions.map((submission, i) => {
          return (
            <TabPanel key={i} value={i}>
              <SyntaxHighlighter
                customStyle={{
                  marginTop: "0rem",
                }}
                language={`${lang[submission.getLangDisplay]['highlight']}`}
                showLineNumbers={true}
                style={vscDarkPlus}>
                {submission.submissionCode.trim()}
              </SyntaxHighlighter>
            </TabPanel>
          )})}
      </TabsUnstyled>
    );
  }
}

class SubmissionsContest extends React.Component {
  render() {
    let submissions = this.props.submissions;
    return (
      <TabsUnstyled defaultValue={0}>
        <TabsList>
          {submissions.map((submission, i) => {
            return (
              <Tab key={i}>{i + 1}</Tab>
            );
          })}
        </TabsList>
        {submissions.map((submission, i) => {
          return (
            <TabPanel key={i} value={i}>
              <SyntaxHighlighter
                customStyle={{
                  marginTop: "0rem",
                }}
                language={`${lang[submission.lang]['highlight']}`}
                showLineNumbers={true}
                style={vscDarkPlus}>
                {`// author: ${submission.username.slice(0, submission.username.length - 3)}\n// rank: ${submission.rank}\n// id: ${submission.id}\n` + submission.code.trim()}
              </SyntaxHighlighter>
            </TabPanel>
          )})}
      </TabsUnstyled>
    );
  }
}

class Problem extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      question_data: null,
      question_note: null,
      submission_details: null,
      submission_contest: null,
      submission_runtime_memory: null,
      question_data_ready: false,
      question_note_ready: false,
      submission_details_ready: false,
      submission_contest_ready: false,
      submission_runtime_memory_ready: false,
    };
  }

  componentDidMount() {
    let baseurl = '/api/' + (this.props.questionId ? `id/${this.props.questionId}` : `title/${this.props.titleSlug}`);
    fetch(`${baseurl}/question_data`)
      .then((response) => response.json())
      .then((data) => {
        if (data && data.question) document.title = `${data.question.questionFrontendId}. ${data.question.title}`;
        this.setState({
          question_data: data['question'],
          question_data_ready: true
        });
      });
    fetch(`${baseurl}/question_note`)
      .then((response) => response.json())
      .then((data) => {
        this.setState({
          question_note: data['question'],
          question_note_ready: true
        });
      });
    fetch(`${baseurl}/submission_runtime_memory`)
      .then((response) => response.json())
      .then((data) => {
        this.setState({
          submission_runtime_memory: data,
          submission_runtime_memory_ready: true,
        });
      });
    fetch(`${baseurl}/submission_details`)
      .then((response) => response.json())
      .then((data) => {
        let submissions = [];
        if (data && data.length > 0) {
          for (let submission of data) {
            if (submission && submission.compare_result && !submission.compare_result.includes('0')) {
              submissions.push(submission);
            }
          }
        }
        this.setState({
          submission_details: submissions,
          submission_details_ready: true,
        });
      });
    fetch(`${baseurl}/submission_contest`)
      .then((response) => response.json())
      .then((data) => {
        let submissions = [];
        if (data && data.length > 0) {
          var count = 0;
          for (let submission of data) {
            if (submission.lang === 'cpp') {
              submissions.push(submission);
              if (++count >= 20) {
                break;
              }
            }
          }
        }
        this.setState({
          submission_contest: submissions,
          submission_contest_ready: true,
        });
      });
  }

  render() {
    let style1 = {
      backgroundColor: `#fff`,
      color: `${gray[100]}`,
      borderRadius: `5px`,
      paddingTop: `5px`,
      paddingBottom: `5px`,
      position: 'sticky',
      top: '0'
    };
    let style2 = {
      borderRadius: `5px`,
      '&:hover': {
        backgroundColor: `#ddd`,
        color: `#222`
      },
      '&:active': {
        backgroundColor: `#fff`,
        color: `#000`
      },
    };
    return (
      <div>
        {this.state.question_data_ready && this.state.question_data
          ? <QuestionTitle question_data={this.state.question_data} />
          : <div>Title</div>
        }
        <TabsUnstyled defaultValue={0}>
          <TabsList style={style1} >
            <Tab style={style2}>Problem Statement</Tab>
            <Tab style={style2}>Solution</Tab>
            <Tab style={style2}>User Submissions</Tab>
            <Tab style={style2}>Runtime / Memory</Tab>
            <Tab style={style2}>Contest Submissions</Tab>
          </TabsList>
          <TabPanel value={0}>
            {this.state.question_data_ready && this.state.question_data
              ? <ProblemStatement question_data={this.state.question_data} />
              : <div></div>
            }
          </TabPanel>
          <TabPanel value={1}>
            {this.state.question_note_ready && this.state.question_note && this.state.question_note.solution && this.state.question_note.solution.content
              ? <Solution question_data={this.state.question_data} question_note={this.state.question_note} />
              : <div></div>
            }
          </TabPanel>
          <TabPanel value={2}>
            {this.state.submission_details_ready && this.state.submission_details && this.state.submission_details.length > 0
              ? <UserSubmissions submissions={this.state.submission_details} />
              : <div></div>
            }
          </TabPanel>
          <TabPanel value={3}>
            {this.state.submission_runtime_memory_ready && this.state.submission_runtime_memory
              ? <SubmissionsRuntimeMemory submission_runtime_memory={this.state.submission_runtime_memory}/>
              : <div></div>
            }
          </TabPanel>
          <TabPanel value={4}>
            {this.state.submission_contest_ready && this.state.submission_contest && this.state.submission_contest.length > 0
              ? <SubmissionsContest submissions={this.state.submission_contest}/>
              : <div></div>
            }
          </TabPanel>
        </TabsUnstyled>
      </div>
    );
  }
};

function ProblemHome() {
  let { problemId, titleSlug } = useParams();
  return (
    <div>
      <TemporaryDrawer />
      <Problem questionId={problemId} titleSlug={titleSlug}/>
    </div>
  );
}

class ProblemList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      dataReady: false,
      list: null
    }
  }

  componentDidMount() {
    let listType = this.props.listType;
    fetch(`/api/list/${listType}`)
      .then((response) => response.json())
      .then((data) => {
        document.title = `${data.type} - ${data.name}`;
        this.setState({
          dataReady: true,
          list: data
        });
      })
  }

  render() {
    let list = this.state.list;
    if (this.state.dataReady && list) {
      return (
        <div>
          <h2>{list.type} - {list.name}</h2>
          <table>
            <thead style={{position: 'sticky', top: '0'}}>
              <tr>
                <th>Status</th>
                <th>Title</th>
                <th>Has Solution</th>
                <th>AC Rate</th>
                <th>Difficulty</th>
              </tr>
            </thead>
            <tbody>
              {list.questions.map((question, i) => {
                return (
                  <tr key={i}>
                    <td><span className="prop"> <i className={question.status === 'ac' ? "fas fa-check" : ""}></i> </span></td>
                    <td style={{textAlign: 'left'}}>
                      <Link href={`${basename}/problems/${question.titleSlug}`} color="inherit" underline="none">
                        {question.frontendQuestionId}. {question.title}
                        <span className="prop"> <i className={question.paidOnly? "fas fa-lock" : ""}></i> </span>
                      </Link>
                    </td>
                    <td><span className="prop"> {question.hasSolution ?  <i className="fas fa-book"></i> : <i></i>}</span></td>
                    <td><div className="progressbar"><div style={{width: question.acRate}}></div></div></td>
                    <td><span className={`${question.difficulty.toLowerCase()}`}>{question.difficulty}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    } else {
      return (
        <div></div>
      );
    }
  }
}

class AllProblemList extends React.Component {
  render() {
    let listSummary = this.props.listSummary;
    // let keys = Object.keys(listSummary);
    let keys = ['Category', 'Difficulty', 'Company', 'Topic', 'Curated Lists', 'Interview', 'Miscellaneous']
    return (
      <TabsUnstyled defaultValue={0}>
        <TabsList
          style={{
            backgroundColor: `#fff`,
            color: `${gray[100]}`,
            borderRadius: `5px`,
            paddingBottom: `5px`,
          }}
        >
          {keys && keys.map((key, i) => {
            return (
              <Tab
                key={i}
                style={{
                  borderRadius: `5px`,
                  '&:hover': {
                    backgroundColor: `#ddd`,
                    color: `#222`
                  },
                  '&:active': {
                    backgroundColor: `#fff`,
                    color: `#000`
                  },
                }}
              >{key}</Tab>
            );
          })}
        </TabsList>
        {keys && keys.map((key, i) => {
          return (
            <TabPanel key={i} value={i}>
              <Grid container spacing={1} columns={16}>
                {listSummary[key].map((list, i) => {
                  return (
                    <Grid  key={i} container item xs="auto">
                      <Link key={i} href={`${basename}/list/${list.slug}`} color="inherit" underline="none">
                        <Chip label={`${list.name} | ${list.number}`} onClick={() => {}} variant="outlined" />
                      </Link>
                    </Grid>
                  );
                })}
              </Grid>
            </TabPanel>
          )})}
      </TabsUnstyled>
    );
  }
}

export default function TemporaryDrawer() {
  const [state, setState] = React.useState({
    top: false,
    dataReady: false,
    listSummary: null,
  });

  if (!state.dataReady) {
    fetch('/api/list')
      .then((response) => response.json())
      .then((data) => {
        setState({
          dataReady: true,
          listSummary: data
        });
      });
  }

  const toggleDrawer = (anchor, open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }

    setState({ ...state, [anchor]: open });
  };

  const list = (anchor) => (
    <Box
      sx={{ width: '1200px', margin: 'auto', padding: 'auto', paddingTop: '20px', paddingBottom: '20px'}}
      role="presentation"
      onClick={toggleDrawer(anchor, true)}
      onKeyDown={toggleDrawer(anchor, true)}
    >
      {state.dataReady && state.listSummary
        ? <AllProblemList dataReady={state.dataReady} listSummary={state.listSummary}/>
        : <div>Loading Data...</div>
      }
    </Box>
  );

  return (
    <div>
      {['top'].map((anchor) => (
        <React.Fragment key={anchor}>
          <Button onClick={toggleDrawer(anchor, true)} style={{marginLeft: '0', paddingLeft: '0', paddingTop: '20px', paddingBottom: '0px', fontSize: '18px', color: 'black'}}>Lists</Button>
          <Drawer
            className='drawer'
            anchor={anchor}
            open={state[anchor]}
            onClose={toggleDrawer(anchor, false)}
          >
            {list(anchor)}
          </Drawer>
        </React.Fragment>
      ))}
    </div>
  );
}

class Playground extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      playground: null,
      playground_ready: false,
    };
  }

  componentDidMount() {
    fetch(`/api/title/${this.props.titleSlug}/playground/${this.props.playgroundId}`)
      .then((response) => response.json())
      .then((data) => {
        this.setState({
          playground: data,
          playground_ready: true,
        });
      });
  }

  render() {
    let playground_ready = this.state.playground_ready;
    let playground = this.state.playground;
    if (playground_ready && playground && playground.allPlaygroundCodes) {
      return (
        <TabsUnstyled defaultValue={0}>
          <TabsList style={{position: 'sticky', top: '0'}}>
            {playground && playground.allPlaygroundCodes && playground.allPlaygroundCodes.map((code, i) => {
              return (
                <Tab key={i}>{lang[code.langSlug]['name']}</Tab>
              );
            })}
          </TabsList>
          {playground && playground.allPlaygroundCodes && playground.allPlaygroundCodes.map((code, i) => {
            return (
              <TabPanel key={i} value={i}>
                <SyntaxHighlighter
                  customStyle={{
                    marginTop: "0rem",
                  }}
                  language={`${lang[code.langSlug]['highlight']}`}
                  showLineNumbers={true}
                  style={vscDarkPlus}>
                  {code.code.trim()}
                </SyntaxHighlighter>
              </TabPanel>
            );
          })}
        </TabsUnstyled>
      );
    } else {
      return (
        <div></div>
      );
    }
  }
}

function ProblemListHome() {
  let { listType } = useParams();
  if (!listType) {
    listType = 'all';
  }
  return (
    <div>
      <TemporaryDrawer style={{position: 'stick', top: '0'}}/>
      <ProblemList listType={listType} />
    </div>
  );
}

function PlaygroundHome() {
  let { titleSlug, playgroundId } = useParams();
  return (
    <div>
      <Playground titleSlug={titleSlug} playgroundId={playgroundId} />
    </div>
  )
}

class App extends React.Component {
  render() {
    return (
      <BrowserRouter basename={basename}>
        <Routes>
          <Route path='/' element={<ProblemListHome />}/>
          <Route path='/problems/' element={<ProblemListHome />}/>
          <Route path='/list/:listType' element={<ProblemListHome />}/>
          <Route path='/problems/id/:problemId' element = {<ProblemHome />} />
          <Route path='/problems/:titleSlug' element = {<ProblemHome />} />
          <Route path='/problems/:titleSlug/playground/:playgroundId' element = {<PlaygroundHome />} />
        </Routes>
      </BrowserRouter>
    );
  }
};

ReactDOM.render(
  <App />,
  document.getElementById('root')
);
